import axios from 'axios';
import type {
  Achievement,
  ApiResponse,
  Assignment,
  EmailPreferences,
  Game,
  LabsSettings,
  OrgGameLibraryPreferences,
  OrgNotice,
  OrganizationDetails,
  OrganizationStats,
  OrganizationUserDirectoryInput,
  OrganizationUserDirectoryPage,
  Stat,
  StatType,
  TeacherUsage,
  Unit,
  UnitStudent,
  User,
  UserAchievement,
} from '@/types';
import { useDemoStore } from '@/stores/demoStore';
import { handleDemoRequest } from '@/demo/demoHandlers';
import { API_BASE_URL } from '@/config/api';
import { createLabsReviewApi, type LabsPost } from '@ludolio/shared-labs';
import type {
  Activity,
  ActivityResponse,
  AddStaffResult,
  AddStudentsResult,
  CreateUnitInput,
  Game as UnitGame,
  Unit as SharedUnit,
  UnitsUser,
  UpdateUnitInput,
} from '@ludolio/shared-units';

class ApiClient {
  public client: ReturnType<typeof axios.create>;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Enable sending cookies with requests
    });

    // Short-circuit all requests when the demo session is active — everything resolves from in-memory fixtures.
    this.client.interceptors.request.use((config) => {
      if (useDemoStore.getState().isDemoMode) {
        config.adapter = async (cfg) => {
          const body = typeof cfg.data === 'string' ? (() => { try { return JSON.parse(cfg.data); } catch { return {}; } })() : (cfg.data ?? {});
          const data = handleDemoRequest(cfg.url || '', body);
          return { data, status: 200, statusText: 'OK', headers: {}, config: cfg, request: {} } as any;
        };
      }
      return config;
    });
  }

  // Token methods are kept for backward compatibility but are no-ops
  setToken(_token: string) {
    // Token is now stored in HttpOnly cookie by the backend
    // This method is kept for backward compatibility
  }

  clearToken() {
    // Token is cleared by the backend when logout is called
    // This method is kept for backward compatibility
  }

  getToken() {
    // Token is stored in HttpOnly cookie, not accessible from JS
    return null;
  }

  // Auth endpoints
  async sendOtp(email: string): Promise<ApiResponse<{ success: boolean; message: string; isNewUser: boolean }>> {
    // Use librarian-specific endpoint that validates email is a librarian or in whitelist
    const response = await this.client.post('/auth/librarian/send-otp', { email });
    return response.data;
  }

  async startInstitutionalLogin(email: string, target: string): Promise<ApiResponse<{
    action: 'redirect_sso';
    redirectUrl: string;
    provider: string;
  } | {
    action: 'otp_sent';
    success: boolean;
    message: string;
    isNewUser: boolean;
  }>> {
    const response = await this.client.post('/auth/institutional-login', { email, portal: 'librarian', client: 'web', target });
    return response.data;
  }

  async verifyOtp(email: string, token: string, firstName?: string, lastName?: string, orgUsername?: string): Promise<ApiResponse<{ user: User }>> {
    const payload: any = { email, token };
    if (firstName) {
      payload.firstName = firstName;
    }
    if (lastName) {
      payload.lastName = lastName;
    }
    if (orgUsername) {
      payload.orgUsername = orgUsername;
    }
    const response = await this.client.post('/auth/librarian/verify-otp', payload);
    return response.data;
  }

  async updateName(firstName: string, lastName: string, orgUsername?: string): Promise<ApiResponse<{ user: User }>> {
    const payload: any = { firstName, lastName };
    if (orgUsername !== undefined) {
      payload.orgUsername = orgUsername;
    }
    const response = await this.client.post('/auth/update-name', payload);
    return response.data;
  }

  async getEmailPreferences(): Promise<ApiResponse<EmailPreferences>> {
    const response = await this.client.post('/auth/email-preferences/get');
    return response.data;
  }

  async updateEmailPreferences(
    preferences: Partial<EmailPreferences>,
  ): Promise<ApiResponse<EmailPreferences>> {
    const response = await this.client.post('/auth/email-preferences/update', preferences);
    return response.data;
  }

  async getOnboardingInfo(): Promise<ApiResponse<{ firstName: string; lastName: string; orgName: string | null; notice: { id: string; title: string; url: string } | null }>> {
    const response = await this.client.post('/auth/onboarding-info');
    return response.data;
  }

  async completeOnboarding(input: { firstName: string; lastName: string; marketingConsent: boolean; privacyAccepted: boolean; orgNoticeId?: string | null }): Promise<ApiResponse<{ user: User }>> {
    const response = await this.client.post('/auth/complete-onboarding', input);
    return response.data;
  }

  async acceptOrgNotice(orgNoticeId: string): Promise<ApiResponse<{ user: User }>> {
    const response = await this.client.post('/auth/accept-org-notice', { orgNoticeId });
    return response.data;
  }

  async getOrganization(organizationId: string): Promise<ApiResponse<OrganizationDetails>> {
    const response = await this.client.post('/organizations/get', { organizationId });
    return response.data;
  }

  async updateOrganization(
    organizationId: string,
    data: { region?: string | null },
  ): Promise<ApiResponse<{ id: string; name: string; region?: string | null }>> {
    const response = await this.client.post('/organizations/update', { organizationId, ...data });
    return response.data;
  }

  async getOrganizationStats(organizationId: string): Promise<ApiResponse<OrganizationStats>> {
    const response = await this.client.post('/organizations/stats', { organizationId });
    return response.data;
  }

  async getOrganizationUserDirectory(
    input: OrganizationUserDirectoryInput
  ): Promise<ApiResponse<OrganizationUserDirectoryPage>> {
    const response = await this.client.post('/organizations/users/list', input);
    return response.data;
  }

  // Organization notice endpoints (first-login onboarding document)
  async getOrgNotice(organizationId: string): Promise<ApiResponse<OrgNotice | null>> {
    const response = await this.client.post('/organizations/notice/get', { organizationId });
    return response.data;
  }

  async uploadOrgNotice(organizationId: string, title: string, file: File): Promise<ApiResponse<OrgNotice>> {
    // Field order matters: @fastify/multipart exposes fields that appear before the file part
    const form = new FormData();
    form.append('organizationId', organizationId);
    form.append('title', title);
    form.append('file', file);
    const response = await this.client.post('/organizations/notice/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async saveOrgNoticeLink(
    organizationId: string,
    title: string,
    url: string
  ): Promise<ApiResponse<OrgNotice>> {
    const response = await this.client.post('/organizations/notice/link', {
      organizationId,
      title,
      url,
    });
    return response.data;
  }

  async deleteOrgNotice(organizationId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await this.client.post('/organizations/notice/delete', { organizationId });
    return response.data;
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.client.post('/auth/logout');
    this.clearToken();
    return response.data;
  }

  async validateToken(): Promise<ApiResponse<{ valid: boolean; user: User }>> {
    const response = await this.client.post('/auth/validate');
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.client.post('/auth/me');
    return response.data;
  }

  async checkEmailDomain(email: string): Promise<ApiResponse<{ found: boolean; organization: any; role: string | null }>> {
    const response = await this.client.post('/organizations/check-email-domain', { email });
    return response.data;
  }

  // Game endpoints
  async getAllGames(): Promise<ApiResponse<Game[]>> {
    const response = await this.client.post('/games/list', { origin: 'commercial' });
    return response.data;
  }

  async getGame(gameId: string): Promise<ApiResponse<Game>> {
    const response = await this.client.post('/games/get', { gameId });
    return response.data;
  }

  async createGame(gameData: Partial<Game>): Promise<ApiResponse<Game>> {
    const response = await this.client.post('/games/create', gameData);
    return response.data;
  }

  async updateGame(gameId: string, gameData: Partial<Game>): Promise<ApiResponse<Game>> {
    const response = await this.client.post('/games/update', { gameId, ...gameData });
    return response.data;
  }

  async deleteGame(gameId: string): Promise<ApiResponse<void>> {
    const response = await this.client.post('/games/delete', { gameId });
    return response.data;
  }

  async getOrgGameCatalog(organizationId: string): Promise<ApiResponse<Array<{
    game: Game;
    platformAvailable: boolean;
    availabilitySource: 'global' | 'explicit' | 'none';
    libraryEnabled: boolean;
  }>>> {
    const response = await this.client.post('/organizations/game-catalog', { organizationId });
    return response.data;
  }

  async setOrgLibraryEnabled(organizationId: string, gameId: string, enabled: boolean): Promise<ApiResponse<any>> {
    const response = await this.client.post('/organizations/game-library/update', {
      organizationId,
      gameId,
      enabled,
    });
    return response.data;
  }

  async getOrgGameLibraryPreferences(
    organizationId: string,
  ): Promise<ApiResponse<OrgGameLibraryPreferences>> {
    const response = await this.client.post('/organizations/game-library/preferences/get', {
      organizationId,
    });
    return response.data;
  }

  async updateOrgGameLibraryPreferences(
    organizationId: string,
    preferences: OrgGameLibraryPreferences,
  ): Promise<ApiResponse<OrgGameLibraryPreferences>> {
    const response = await this.client.post('/organizations/game-library/preferences/update', {
      organizationId,
      ...preferences,
    });
    return response.data;
  }

  // Achievement endpoints
  async createAchievement(achievementData: Partial<Achievement>): Promise<ApiResponse<Achievement>> {
    const response = await this.client.post('/achievements/create', achievementData);
    return response.data;
  }

  async getGameAchievements(gameId: string): Promise<ApiResponse<Achievement[]>> {
    const response = await this.client.post('/achievements/game-achievements', { gameId });
    return response.data;
  }

  async getUserAchievements(userId: string, gameId: string, unitId?: string): Promise<ApiResponse<UserAchievement[]>> {
    const response = await this.client.post('/achievements/user-achievements', { userId, gameId, unitId });
    return response.data;
  }

  async getAllUserAchievements(userId: string): Promise<ApiResponse<UserAchievement[]>> {
    const response = await this.client.post('/achievements/all-user-achievements', { userId });
    return response.data;
  }

  // Assignment endpoints
  async createAssignment(assignmentData: {
    studentId: string;
    gameId: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
  }): Promise<ApiResponse<Assignment>> {
    const response = await this.client.post('/assignments/create', assignmentData);
    return response.data;
  }

  async getStudentGames(studentId: string): Promise<ApiResponse<Assignment[]>> {
    const response = await this.client.post('/assignments/student-games', { studentId });
    return response.data;
  }

  async updateAssignment(assignmentId: string, assignmentData: Partial<Assignment>): Promise<ApiResponse<Assignment>> {
    const response = await this.client.post('/assignments/update', { assignmentId, ...assignmentData });
    return response.data;
  }

  async deleteAssignment(assignmentId: string): Promise<ApiResponse<void>> {
    const response = await this.client.post('/assignments/delete', { assignmentId });
    return response.data;
  }

  async validateGameAccess(studentId: string, gameId: string): Promise<ApiResponse<{ hasAccess: boolean; assignment?: Assignment }>> {
    const response = await this.client.post('/assignments/validate-access', { studentId, gameId });
    return response.data;
  }

  // Student endpoints - Get all students in librarian's organization
  async getAllStudents(): Promise<ApiResponse<User[]>> {
    const response = await this.client.post('/users/list-students');
    return response.data;
  }

  // Teacher endpoints - Get all teachers in librarian's organization
  async getAllTeachers(): Promise<ApiResponse<TeacherUsage[]>> {
    const response = await this.client.post('/users/list-teachers');
    return response.data;
  }

  // Teacher email whitelist management
  async addTeacherEmail(email: string, name: string): Promise<ApiResponse<any>> {
    const response = await this.client.post('/users/add-teacher-email', { email, name });
    return response.data;
  }

  async listTeacherEmails(): Promise<ApiResponse<any[]>> {
    const response = await this.client.post('/users/list-teacher-emails', {});
    return response.data;
  }

  async updateTeacherEmail(teacherEmailId: string, email?: string, name?: string): Promise<ApiResponse<any>> {
    const response = await this.client.post('/users/update-teacher-email', {
      teacherEmailId,
      email,
      name
    });
    return response.data;
  }

  async deleteTeacherEmail(teacherEmailId: string): Promise<ApiResponse<any>> {
    const response = await this.client.post('/users/delete-teacher-email', { teacherEmailId });
    return response.data;
  }

  async resendInstructorInvitation(
    teacherEmailId: string
  ): Promise<ApiResponse<{ jobId: string | null; status: string; created: boolean }>> {
    const response = await this.client.post('/users/resend-instructor-invitation', { teacherEmailId });
    return response.data;
  }

  async bulkAddTeacherEmails(teachers: Array<{ email: string; name: string }>): Promise<ApiResponse<any>> {
    const response = await this.client.post('/users/bulk-add-teacher-emails', { teachers });
    return response.data;
  }

  async updateStaffRole(userId: string, newRole: 'staff' | 'teacher'): Promise<ApiResponse<any>> {
    const response = await this.client.post('/users/update-staff-role', { userId, newRole });
    return response.data;
  }

  // Unit endpoints
  async getMyUnits(): Promise<ApiResponse<Unit[]>> {
    const response = await this.client.post('/units/my-units');
    return response.data;
  }

  async getOrganizationUnits(): Promise<ApiResponse<SharedUnit[]>> {
    const response = await this.client.post('/units/org-units', {});
    return response.data;
  }

  async createUnit(data: CreateUnitInput): Promise<ApiResponse<SharedUnit>> {
    const response = await this.client.post('/units/create', data);
    return response.data;
  }

  async getUnitById(unitId: string): Promise<ApiResponse<SharedUnit>> {
    const response = await this.client.post('/units/get', { unitId });
    return response.data;
  }

  async updateUnit(unitId: string, data: UpdateUnitInput): Promise<ApiResponse<SharedUnit>> {
    const response = await this.client.post('/units/update', { unitId, ...data });
    return response.data;
  }

  async deleteUnit(unitId: string, confirmationCode: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/delete', { unitId, confirmationCode });
    return response.data;
  }

  async getUnitStudents(unitId: string): Promise<ApiResponse<UnitStudent[]>> {
    const response = await this.client.post('/units/students', { unitId });
    return response.data;
  }

  async getUnitGames(unitId: string): Promise<ApiResponse<UnitGame[]>> {
    const response = await this.client.post('/units/games', { unitId });
    return response.data;
  }

  async getUnitTeachers(unitId: string): Promise<ApiResponse<UnitsUser[]>> {
    const response = await this.client.post('/units/teachers', { unitId });
    return response.data;
  }

  async addStudentsByEmail(unitId: string, emails: string[]): Promise<ApiResponse<AddStudentsResult>> {
    const response = await this.client.post('/units/add-students-by-email', { unitId, emails });
    return response.data;
  }

  async getInvitationBatchProgress(batchId: string) {
    const response = await this.client.post('/units/invitation-email-batch-status', { batchId });
    return response.data;
  }

  async removePendingEnrollment(unitId: string, pendingId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/remove-pending-enrollment', { unitId, pendingId });
    return response.data;
  }

  async resendStudentInvitation(
    unitId: string,
    pendingId: string
  ): Promise<ApiResponse<{ jobId: string | null; status: string; created: boolean }>> {
    const response = await this.client.post('/units/resend-student-invitation', { unitId, pendingId });
    return response.data;
  }

  async removeStudentFromUnit(unitId: string, studentId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/remove-student', { unitId, studentId });
    return response.data;
  }

  async addTeachersByEmail(unitId: string, emails: string[]): Promise<ApiResponse<AddStaffResult>> {
    const response = await this.client.post('/units/add-teachers-by-email', { unitId, emails });
    return response.data;
  }

  async removeTeacherFromUnit(unitId: string, teacherId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/remove-teacher', { unitId, teacherId });
    return response.data;
  }

  async listActivities(unitId: string): Promise<ApiResponse<Activity[]>> {
    const response = await this.client.post('/activities/list', { unitId });
    return response.data;
  }

  async getActivityResponses(activityId: string): Promise<ApiResponse<ActivityResponse[]>> {
    const response = await this.client.post('/activities/responses', { activityId });
    return response.data;
  }

  // Statistics endpoints
  async getOrgGamesOverview(): Promise<ApiResponse<Array<{
    gameId: string;
    uniquePlayersCount: number;
  }>>> {
    const response = await this.client.post('/statistics/org-games-overview', {});
    return response.data;
  }

  async getUnitStatistics(unitId: string): Promise<ApiResponse<Array<{
    gameId: string;
    gameTitle: string;
    totalPlayingTimeSeconds: number;
    totalSessions: number;
    uniquePlayersCount: number;
    installationCount: number;
  }>>> {
    const response = await this.client.post('/statistics/unit-statistics', { unitId });
    return response.data;
  }

  async getUnitGamePlayingTime(unitId: string, gameId: string): Promise<ApiResponse<Array<{
    userId: string;
    userName: string;
    totalPlayingTimeSeconds: number;
    sessionCount: number;
    lastPlayedAt: string | null;
  }>>> {
    const response = await this.client.post('/statistics/unit-game-playing-time', { unitId, gameId });
    return response.data;
  }

  async getUnitGameInstallations(unitId: string, gameId: string): Promise<ApiResponse<Array<{
    userId: string;
    userName: string;
    installationCount: number;
    lastInstalledAt: string | null;
  }>>> {
    const response = await this.client.post('/statistics/unit-game-installations', { unitId, gameId });
    return response.data;
  }

  async getStudentPlayingTimes(userId: string): Promise<ApiResponse<Array<{
    gameId: string;
    gameTitle: string;
    totalPlayingTimeSeconds: number;
    sessionCount: number;
    lastPlayedAt: string | null;
  }>>> {
    const response = await this.client.post('/statistics/user-playing-times', { userId });
    return response.data;
  }

  // Stat endpoints
  async getGameStats(gameId: string): Promise<ApiResponse<Stat[]>> {
    const response = await this.client.post('/stats/game-stats', { gameId });
    return response.data;
  }

  async createStat(data: {
    gameId: string;
    statId: string;
    name: string;
    type?: StatType;
    incrementOnly?: boolean;
    maxChange?: number;
    minValue?: number;
    maxValue?: number;
    windowSize?: number;
    defaultValue?: string;
    aggregated?: boolean;
    metadata?: Record<string, any>;
  }): Promise<ApiResponse<Stat>> {
    const response = await this.client.post('/stats/create', data);
    return response.data;
  }

  async deleteStat(statId: string): Promise<ApiResponse<any>> {
    const response = await this.client.post('/stats/delete', { statId });
    return response.data;
  }
}

export const apiClient = new ApiClient();

const labsPost: LabsPost = async <T,>(url: string, body?: unknown) => {
  const response = await apiClient.client.post(url, body ?? {});
  return response.data?.data as T;
};

export const labsReviewApi = createLabsReviewApi(labsPost);

export const labsSettingsApi = {
  get: () => labsPost<LabsSettings | null>('/labs/admin/settings/get'),
  update: (input: Partial<Pick<LabsSettings, 'defaultVisibility' | 'reportContactEmail' | 'remixingEnabled'>> & { acceptInternalRemixTerms?: boolean }) =>
    labsPost<LabsSettings>('/labs/admin/settings/update', input),
};

export interface LabQuizNotice {
  id: string;
  version: number;
  body: string | null;
  documentUrl: string | null;
}

export const labsQuizNoticeApi = {
  get: () => labsPost<LabQuizNotice | null>('/labs/admin/quiz-notice/get'),
  publish: (input: { body: string | null; documentUrl: string | null }) =>
    labsPost<LabQuizNotice>('/labs/admin/quiz-notice/publish', input),
};

export interface OrphanedLabTeam {
  id: string;
  name: string;
  description: string | null;
  members: Array<{ userId: string; name: string; email: string; role: 'owner' | 'manager' | 'member'; inOrganization: boolean }>;
  projects: Array<{ id: string; title: string }>;
  projectTotal: number;
}

export interface OrphanedLabTeamPage {
  teams: OrphanedLabTeam[];
  page: number;
  pageSize: number;
  total: number;
}

export const labsTeamAdminApi = {
  listOrphaned: (page = 1) => labsPost<OrphanedLabTeamPage>('/labs/admin/teams/orphaned', { page, pageSize: 20 }),
  assignOwner: (teamId: string, email: string) =>
    labsPost<{ success: boolean }>('/labs/admin/teams/assign-owner', { teamId, email }),
};

